import { useCallback, useEffect, useRef, useState } from 'react';
import * as AttemptModule from 'modules/attempt';
import * as Api from '../api';
import { secondsLeft } from '../constants';
import type { CandidateSection, ExamSnapshot, ExamState } from '../types';

type AnswerValue = unknown;

/** What is waiting to be saved for one question: the answer and/or how long it was open and whether it was marked. */
interface PendingSave {
  value?: AnswerValue;
  timeSpentSec?: number;
  flagged?: boolean;
}

/** What the runner reports about a question besides its answer. */
export interface QuestionMeta {
  /** Seconds spent on the question so far: a running total. */
  timeSpentSec?: number;
  flagged?: boolean;
}

const SAVE_DEBOUNCE_MS = 700;
/** How often queued answers are retried while sending keeps failing. */
const RETRY_SAVE_MS = 8000;
/** Codes the API returns when the open section changed under us. */
const STALE_CODES = new Set(['SECTION_TIME_UP', 'SECTION_LOCKED', 'QUESTION_NOT_IN_SECTION']);

interface Options {
  attemptId: string;
  initialAnswers: Map<string, AnswerValue>;
  onFinished: (status: string) => void;
}

export interface ExamSession {
  loading: boolean;
  error: string | null;
  examState: ExamState | null;
  section: CandidateSection | null;
  answers: Map<string, AnswerValue>;
  /** Seconds left in the open section, or in the break. Null when untimed. */
  remaining: number | null;
  onBreak: boolean;
  busy: boolean;
  saving: boolean;
  /** The last attempt to send answers failed; they stay queued and are retried. */
  saveFailed: boolean;
  /** Retries the queued answers now. */
  retrySave: () => Promise<void>;
  /** Title of the section the clock just closed, until dismissed. */
  timeUpTitle: string | null;
  dismissTimeUp: () => void;
  setAnswer: (questionId: string, value: AnswerValue) => void;
  /** Time spent on a question and its review mark; saved with the answers, and only for an answered question. */
  setMeta: (questionId: string, meta: QuestionMeta) => void;
  /** Runs `settle` just before answers are sent, so a running timer can report its total first. Returns the way to stop. */
  registerSettle: (settle: () => void) => () => void;
  /** Mirrors an answer the server already stored (Speaking recordings) without saving it again. */
  setLocalAnswer: (questionId: string, value: AnswerValue) => void;
  /** Closes the open section. With `hold`, the next one waits for the candidate; the clock running out never holds. */
  advance: (options?: { hold?: boolean }) => Promise<void>;
  resumeBreak: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Candidate runtime for IELTS/SAT attempts. The server owns deadlines and the
 * open section; this hook mirrors them, saves answers, and moves on when the
 * timer runs out. Every call that changes sections is idempotent server-side,
 * so a double click or two tabs cannot skip a section.
 */
export function useExamSession({ attemptId, initialAnswers, onFinished }: Options): ExamSession {
  const [snapshot, setSnapshot] = useState<ExamSnapshot | null>(null);
  const [answers, setAnswers] = useState<Map<string, AnswerValue>>(initialAnswers);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [timeUpTitle, setTimeUpTitle] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const skewRef = useRef(0);
  const pendingRef = useRef(new Map<string, PendingSave>());
  /** The latest time and mark per question, so an answer given after them still carries them (the server never makes an answer from them alone). */
  const metaRef = useRef(new Map<string, QuestionMeta>());
  const settlersRef = useRef(new Set<() => void>());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishedRef = useRef(false);
  const autoStepRef = useRef<string | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const apply = useCallback((next: ExamSnapshot) => {
    if (next.examState?.serverNow) {
      skewRef.current = new Date(next.examState.serverNow).getTime() - Date.now();
    }
    setSnapshot(next);
    if (next.finished && !finishedRef.current) {
      finishedRef.current = true;
      onFinishedRef.current(next.status);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await Api.ExamState(attemptId);
      apply(data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the exam');
    } finally {
      setLoading(false);
    }
  }, [attemptId, apply]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const flush = useCallback(async () => {
    settlersRef.current.forEach((settle) => settle());
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const entries = [...pendingRef.current.entries()];
    if (!entries.length) return;
    pendingRef.current = new Map();
    setSaving(true);
    try {
      await AttemptModule.Api.SaveAnswersBatch(
        attemptId,
        entries.map(([questionId, save]) => ({
          questionId,
          ...(save.value !== undefined ? { value: save.value as any } : {}),
          ...(save.timeSpentSec !== undefined ? { timeSpentSec: save.timeSpentSec } : {}),
          ...(save.flagged !== undefined ? { flagged: save.flagged } : {}),
        })),
      );
      setSaveFailed(false);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (STALE_CODES.has(code)) {
        // The section closed (deadline or another tab): pick up the new state.
        await refresh();
      } else {
        // Keep the answers queued; they are retried when the connection returns, every few seconds, and by the next change or advance.
        for (const [id, v] of entries) pendingRef.current.set(id, { ...v, ...pendingRef.current.get(id) });
        setSaveFailed(true);
      }
    } finally {
      setSaving(false);
    }
  }, [attemptId, refresh]);

  const setAnswer = useCallback(
    (questionId: string, value: AnswerValue) => {
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(questionId, value);
        return next;
      });
      pendingRef.current.set(questionId, { ...metaRef.current.get(questionId), ...pendingRef.current.get(questionId), value });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const setMeta = useCallback(
    (questionId: string, meta: QuestionMeta) => {
      const known = metaRef.current.get(questionId);
      const merged: QuestionMeta = { ...known, ...meta };
      // A running total only grows, as on the server.
      if (meta.timeSpentSec !== undefined) merged.timeSpentSec = Math.max(known?.timeSpentSec ?? 0, meta.timeSpentSec);
      metaRef.current.set(questionId, merged);
      pendingRef.current.set(questionId, {
        ...pendingRef.current.get(questionId),
        ...meta,
        ...(meta.timeSpentSec !== undefined ? { timeSpentSec: merged.timeSpentSec } : {}),
      });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const registerSettle = useCallback((settle: () => void) => {
    settlersRef.current.add(settle);
    return () => {
      settlersRef.current.delete(settle);
    };
  }, []);

  const setLocalAnswer = useCallback((questionId: string, value: AnswerValue) => {
    setAnswers((prev) => {
      const next = new Map(prev);
      next.set(questionId, value);
      return next;
    });
  }, []);

  const advance = useCallback(async (options?: { hold?: boolean }) => {
    const sectionId = snapshot?.examState?.activeSectionId;
    if (!sectionId || busy) return;
    setBusy(true);
    try {
      await flush();
      const { data } = options?.hold ? await Api.AdvanceSection(attemptId, sectionId, true) : await Api.AdvanceSection(attemptId, sectionId);
      apply(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not move to the next section');
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [snapshot, busy, flush, attemptId, apply, refresh]);

  const resumeBreak = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await Api.ResumeBreak(attemptId);
      apply(data);
    } catch {
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [busy, attemptId, apply, refresh]);

  // One clock for the whole runner.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Unsent answers go out as soon as the connection is back, and are tried again every few seconds while saving keeps failing.
  useEffect(() => {
    const retry = () => {
      void flush();
    };
    window.addEventListener('online', retry);
    const id = saveFailed ? setInterval(retry, RETRY_SAVE_MS) : null;
    return () => {
      window.removeEventListener('online', retry);
      if (id) clearInterval(id);
    };
  }, [flush, saveFailed]);

  // Save on tab hide / close so nothing typed is lost.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [flush]);

  const examState = snapshot?.examState ?? null;
  const onBreak = Boolean(examState?.breakUntil && !examState.activeSectionId);
  const activeSection = examState?.sections.find((s) => s.id === examState.activeSectionId);
  const deadline = onBreak ? examState?.breakUntil : activeSection?.deadlineAt;
  const remaining = secondsLeft(deadline, skewRef.current, now);

  // Time is up: close the section (or end the break) once per section.
  useEffect(() => {
    if (remaining !== 0 || busy) return;
    const key = onBreak ? `break:${examState?.breakUntil}` : `section:${examState?.activeSectionId}`;
    if (autoStepRef.current === key) return;
    autoStepRef.current = key;
    if (onBreak) resumeBreak();
    else {
      setTimeUpTitle(activeSection?.title ?? '');
      advance();
    }
  }, [remaining, busy, onBreak, examState, activeSection?.title, advance, resumeBreak]);

  return {
    loading,
    error,
    examState,
    section: snapshot?.section ?? null,
    answers,
    remaining,
    onBreak,
    busy,
    saving,
    saveFailed,
    retrySave: flush,
    timeUpTitle,
    dismissTimeUp: () => setTimeUpTitle(null),
    setAnswer,
    setMeta,
    registerSettle,
    setLocalAnswer,
    advance,
    resumeBreak,
    refresh,
  };
}
